import { useLocalization } from '@lib/localization';
import { Box, CardContent, FormControl, Grid2, InputLabel, MenuItem, Paper, Select } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { showSnackbar } from '@store/reducers/snackbarSlice';
import { type FC, useEffect, useState } from 'react';
import { CardDescription, CardHeader, CardTitle } from './ServerConfig.styled';

const TIME_RANGES = {
  months: Array.from({ length: 13 }, (_, i) => i),
  days: Array.from({ length: 31 }, (_, i) => i),
  hours: Array.from({ length: 25 }, (_, i) => i),
  minutes: Array.from({ length: 61 }, (_, i) => i),
};

const TIME_CONSTANTS = {
  secondsInMinute: 60,
  secondsInHour: 60 * 60,
  secondsInDay: 24 * 60 * 60,
  secondsInMonth: 30 * 24 * 60 * 60,
};

interface SessionExpirationCardProps {
  value: number
  onChanged?(value: number): void
}

const SessionExpirationCard: FC<SessionExpirationCardProps> = ({ value, onChanged }) => {
  const [months, setMonths] = useState(0);
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(1);
  const [minutes, setMinutes] = useState(0);
  const dispatch = useAppDispatch();

  const { t } = useLocalization();

  const hasUpdatePermission = (useAppSelector((state) => state.auth).user?.role ?? 'user') == 'sysadmin';

  useEffect(() => {
    const setStateValues = (seconds: number) => {
      let remainingSeconds = seconds;
      const months = Math.floor(remainingSeconds / TIME_CONSTANTS.secondsInMonth);
      remainingSeconds %= TIME_CONSTANTS.secondsInMonth;
      const days = Math.floor(remainingSeconds / TIME_CONSTANTS.secondsInDay);
      remainingSeconds %= TIME_CONSTANTS.secondsInDay;
      const hours = Math.floor(remainingSeconds / TIME_CONSTANTS.secondsInHour);
      remainingSeconds %= TIME_CONSTANTS.secondsInHour;
      const minutes = Math.floor(remainingSeconds / TIME_CONSTANTS.secondsInMinute);

      setMonths(months);
      setDays(days);
      setHours(hours);
      setMinutes(minutes);
    };

    setStateValues(value);
  }, [value]);

  interface TimeComponents {
    months?: number
    days?: number
    hours?: number
    minutes?: number
  }

  const createChangeHandler = (key: 'months' | 'days' | 'hours' | 'minutes') => (value: number) => {
    const newValues = { months, days, hours, minutes };

    if (key === 'months') {
      newValues.months = value;

      if (value === 12 && (newValues.days || newValues.hours || newValues.minutes)) {
        dispatch(
          showSnackbar({
            message: t('ServerConfig_SessionExpiration_Exceed_Warning'),
            severity: 'warning',
          }),
        );

        newValues.days = 0;
        newValues.hours = 0;
        newValues.minutes = 0;
      }
    } else {
      if (value !== 0 && newValues.months === 12) {
        dispatch(
          showSnackbar({
            message: t('ServerConfig_SessionExpiration_Exceed_Warning'),
            severity: 'warning',
          }),
        );

        newValues.months = 11;
      }
      newValues[key] = value;
    }

    let seconds = calculateTotalSeconds(newValues);

    if (seconds < 60 * 60) {
      dispatch(
        showSnackbar({
          message: t('ServerConfig_SessionExpiration_Less_Warning'),
          severity: 'warning',
        }),
      );

      seconds = 60 * 60;
    }

    onChanged?.(seconds);
  };

  const calculateTotalSeconds = (time: TimeComponents) => {
    const { months = 0, days = 0, hours = 0, minutes = 0 } = time;

    return (
      months * TIME_CONSTANTS.secondsInMonth +
      days * TIME_CONSTANTS.secondsInDay +
      hours * TIME_CONSTANTS.secondsInHour +
      minutes * TIME_CONSTANTS.secondsInMinute
    );
  };

  const onMonthChange = createChangeHandler('months');
  const onDayChange = createChangeHandler('days');
  const onHourChange = createChangeHandler('hours');
  const onMinuteChange = createChangeHandler('minutes');

  const timeFields = [
    { label: 'Months', value: months, onChange: onMonthChange, options: TIME_RANGES.months },
    { label: 'Days', value: days, onChange: onDayChange, options: TIME_RANGES.days },
    { label: 'Hours', value: hours, onChange: onHourChange, options: TIME_RANGES.hours },
    { label: 'Minutes', value: minutes, onChange: onMinuteChange, options: TIME_RANGES.minutes },
  ];

  return (
    <Paper sx={{ mb: 2 }}>
      <CardHeader>
        <Box>
          <CardTitle variant="h6">{t('ServerConfig_SessionExpiration')}</CardTitle>
          <CardDescription variant="body2">{t('ServerConfig_SessionExpiration_Description')}</CardDescription>
        </Box>
      </CardHeader>
      <CardContent style={{ padding: '24px' }}>
        <Grid2 container spacing={2}>
          {timeFields.map(({ label, value, onChange, options }) => (
            <Grid2 size={{ xs: 6, sm: 3 }} key={label}>
              <Box>
                <FormControl fullWidth size="small">
                  <InputLabel id={`${label}-select-label`}>{t(`Common_${label}`)}</InputLabel>
                  <Select<number>
                    labelId={`${label}-select-label`}
                    label={t(`Common_${label}`)}
                    value={value}
                    disabled={!hasUpdatePermission}
                    onChange={(e) => onChange(e.target.value as number)}>
                    {options.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid2>
          ))}
        </Grid2>
      </CardContent>
    </Paper>
  );
};

export default SessionExpirationCard;
